import { useState, useEffect } from 'react';
import { getActivityTypes } from '../../../visualization/LeadTimeHistogram/api/leadTimeHistogramApi';

/**
 * Custom hook for fetching and managing activity types for dropdowns.
 * Follows the same pattern as useObjectTypes from LeadTimeHistogram.
 * 
 * @param {string} activeFile - The currently active file
 * @returns {object} Activity types state and loading status
 */
export const useActivityTypes = (activeFile) => {
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivityTypes = async () => {
      if (!activeFile) {
        setActivityTypes([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('🔄 Fetching activity types for file:', activeFile);
        const types = await getActivityTypes(activeFile);
        
        if (Array.isArray(types) && types.length > 0) {
          setActivityTypes(types);
          console.log('✅ Activity types loaded:', types);
        } else {
          // Fallback to common activity types if none found
          const fallbackTypes = [
            'Angebot anlegen',
            'Auftrag anlegen (aus Angebot)',
            'Auftrag anlegen',
            'Lieferschein erstellen',
            'Preisänderung Position',
            'Preisänderung Auftragskopf',
            'Auftragspositionen anlegen'
          ];
          setActivityTypes(fallbackTypes);
          console.log('⚠️ No activity types found, using fallback:', fallbackTypes);
        }
      } catch (err) {
        console.error('❌ Error fetching activity types:', err);
        setError(err.message || 'Failed to fetch activity types');
        
        // Set fallback types even on error
        const fallbackTypes = [
          'Angebot anlegen',
          'Auftrag anlegen (aus Angebot)',
          'Auftrag anlegen',
          'Lieferschein erstellen',
          'Preisänderung Position',
          'Preisänderung Auftragskopf',
          'Auftragspositionen anlegen'
        ];
        setActivityTypes(fallbackTypes);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityTypes();
  }, [activeFile]);

  // Reset when activeFile changes
  useEffect(() => {
    if (!activeFile) {
      setActivityTypes([]);
      setError(null);
    }
  }, [activeFile]);

  return {
    activityTypes,
    loading,
    error,
    hasData: activityTypes.length > 0
  };
};